import { useMediaQuery, useTheme } from '@mui/material';
import { Identifier } from '@okletsplay/core';
import { ModalName } from 'app/enums';
import { UserEntity } from 'app/interfaces/entity';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { useCancelToken } from 'app/services/api/hooks';
import { modalsReducers } from 'app/store/modal/slice';
import { sessionSelector } from 'app/store/session/selectors';
import { dispatchUnwrap } from 'app/utils/store';
import { ChatSessionContext } from 'modules/chat/providers/ChatSessionProvider';
import { createChatUser } from 'modules/chat/utils';
import { updateUserConversation } from 'modules/users/store/users/repository';
import React, {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Talk from 'talkjs';

interface PlayerContextProps {
  isLoaded: boolean;
  isPopupOpen: boolean;
  isPopupHidden: boolean;
  recipient: UserEntity | null;
  conversation: string | null | Talk.ConversationBuilder;
  recipientId: string | null;
  setIsPopupOpen: Dispatch<SetStateAction<boolean>>;
  setIsPopupHidden: Dispatch<SetStateAction<boolean>>;
  resetChat: () => void;
  updateRecipientId: (data: null | string) => void;
  updateConversation: (data: null | string | Talk.ConversationBuilder) => void;
  setParticipant: (data: Talk.UserData | null) => void;
  participant: Talk.UserData | null;
}

export const ChatBoxContext = createContext<PlayerContextProps>({
  recipient: null,
  isPopupOpen: false,
  isPopupHidden: true,
  isLoaded: false,
  conversation: null,
  recipientId: null,
  resetChat: () => {},
  setIsPopupOpen: () => {},
  setIsPopupHidden: () => {},
  updateRecipientId: () => {},
  updateConversation: () => {},
  participant: null,
  setParticipant: () => {},
});

export const ChatBoxProvider: FC = ({ children }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const cancelGetUserToken = useCancelToken();
  const { session } = useContext(ChatSessionContext);

  const isFullScreen = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const sender = useSelector(sessionSelector.activeUser) as UserEntity;
  const isChatPinned = useSelector(sessionSelector.isChatPinned);

  const [recipientId, setRecipientId] = useState<null | string>(null);
  const [conversation, setConversation] = useState<null | string | Talk.ConversationBuilder>(null);
  const [recipient, setRecipient] = useState<null | UserEntity>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [isPopupHidden, setIsPopupHidden] = useState<boolean>(false);
  const [participant, setParticipant] = useState<Talk.UserData | null>(null);

  const userId = useMemo(() => {
    if (!sender) {
      return null;
    }

    return sender.id as Identifier;
  }, [sender]);

  const resetChat = useCallback(() => {
    setRecipientId(null);
    setConversation(null);
  }, []);

  const openModal = useCallback(() => {
    if (!isChatPinned || !isFullScreen) {
      dispatch(modalsReducers.openModal({ name: ModalName.Chat }));
    }

    if (isFullScreen && isChatPinned) {
      dispatch(modalsReducers.closeModal(ModalName.Chat));
      setIsPopupOpen(true);
      setIsPopupHidden(false);
    }
  }, [dispatch, isFullScreen, isChatPinned]);

  const updateConversation = useCallback(
    (data: null | string | Talk.ConversationBuilder) => {
      openModal();
      setConversation(data);
      setIsLoaded(true);
      setRecipientId(null);
      setRecipient(null);
    },
    [openModal]
  );
  const updateRecipientId = useCallback(
    (data: null | string) => {
      openModal();
      setRecipient(null);
      setConversation(null);
      setRecipientId(data);
    },
    [openModal]
  );

  const createConversation = useCallback(
    (recipientData: UserEntity | null) => {
      if (!sender || !recipientData || !session) {
        return null;
      }

      const senderUser = createChatUser(sender);
      const recipientUser = createChatUser(recipientData);
      if (!recipientUser || !senderUser) {
        return null;
      }

      const access = recipientData?.chat?.isMuted ? 'Read' : 'ReadWrite';
      const conversationIdToReceive = Talk.oneOnOneId(senderUser, recipientUser);
      const newConversation = session.getOrCreateConversation(conversationIdToReceive);
      newConversation.setParticipant(senderUser);
      newConversation.setParticipant(recipientUser, { access });
      setParticipant(recipientUser as Talk.UserData);
      setRecipient(recipientData);

      return newConversation;
    },
    [sender, setRecipient, session]
  );

  useEffect(() => {
    if (recipientId) {
      setIsLoaded(false);
      apiRequestCall<UserEntity>({
        params: {
          method: 'GET',
          resource: ApiResource.Users,
          urlPrefix: recipientId,
          cancelToken: cancelGetUserToken,
        },
        handlers: {
          success: (data) => {
            setIsLoaded(true);
            const newConversation = createConversation(data);
            setConversation(newConversation);
          },
        },
        rejectOnCancel: false,
      });
    }
  }, [cancelGetUserToken, createConversation, recipientId, setIsLoaded]);

  useEffect(() => {
    if ((recipientId || conversation) && userId && !sender?.hasConversations) {
      dispatchUnwrap(updateUserConversation({ userId }), {}, false);
    }
  }, [userId, conversation, sender?.hasConversations, recipientId]);

  return (
    <ChatBoxContext.Provider
      value={{
        resetChat,
        recipientId,
        conversation,
        isPopupOpen,
        isPopupHidden,
        setIsPopupOpen,
        setIsPopupHidden,
        updateConversation,
        updateRecipientId,
        isLoaded,
        recipient,
        participant,
        setParticipant,
      }}
    >
      {children}
    </ChatBoxContext.Provider>
  );
};
